/**
 * Header Component
 */
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleThemeMode, setSidebarOpen, setLanguage } from '../../store/slices/uiSlice';
import {
  NAV_DRAWER_WIDTH,
  NAV_DRAWER_WIDTH_COLLAPSED,
  MOBILE_NAV_BREAKPOINT,
} from './layoutConstants';
import { logout } from '../../store/slices/authSlice';
import { useLogoutMutation } from '../../store/api/authApi';

const Header = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user, refreshToken } = useAppSelector((state) => state.auth);
  const { themeMode, sidebarCollapsed, language } = useAppSelector((state) => state.ui);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageChange = (lang: 'en' | 'ar') => {
    dispatch(setLanguage(lang));
    setLangMenuAnchor(null);
  };
  const [logoutMutation] = useLogoutMutation();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_NAV_BREAKPOINT));
  const desktopOffset = sidebarCollapsed ? NAV_DRAWER_WIDTH_COLLAPSED : NAV_DRAWER_WIDTH;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      // Call logout API endpoint with refresh token if available
      await logoutMutation({
        refresh_token: refreshToken || undefined,
      }).unwrap();
      
      // Clear local state and navigate to login
      dispatch(logout());
      navigate('/login');
      toast.success(t('header.logout') || 'Logged out successfully');
    } catch (error) {
      // Even if API call fails, clear local state and navigate
      // The token will expire naturally
      dispatch(logout());
      navigate('/login');
    }
  };


  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        left: { xs: 0, md: desktopOffset },
        width: { xs: '100%', md: `calc(100% - ${desktopOffset}px)` },
        zIndex: theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => dispatch(setSidebarOpen(true))}
            sx={{ mr: 1, color: theme.palette.text.primary }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Language Switcher */}
        <IconButton
          onClick={(e) => setLangMenuAnchor(e.currentTarget)}
          sx={{ color: theme.palette.text.secondary }}
          title={language === 'en' ? 'العربية' : 'English'}
          aria-label="Change language"
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langMenuAnchor}
          open={Boolean(langMenuAnchor)}
          onClose={() => setLangMenuAnchor(null)}
          PaperProps={{
            elevation: 3,
            sx: { mt: 1, minWidth: 120, borderRadius: 2 },
          }}
        >
          <MenuItem
            onClick={() => handleLanguageChange('en')}
            selected={language === 'en'}
            sx={{ direction: 'ltr' }}
          >
            English
          </MenuItem>
          <MenuItem
            onClick={() => handleLanguageChange('ar')}
            selected={language === 'ar'}
            sx={{ direction: 'rtl' }}
          >
            العربية
          </MenuItem>
        </Menu>

        {/* Theme Toggle */}
        <IconButton
          onClick={() => dispatch(toggleThemeMode())}
          sx={{ color: theme.palette.text.secondary, ml: 1 }}
        >
          {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* Notifications */}
        {/* <IconButton sx={{ color: theme.palette.text.secondary, ml: 1 }}>
          <NotificationsIcon />
        </IconButton> */}

        {/* User Menu */}
        <Box sx={{ ml: 2 }}>
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            <Avatar
              src={user?.profile_picture || undefined}
              alt={user?.full_name || user?.first_name || 'User'}
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
              }}
            >
              {user?.first_name?.charAt(0) || user?.full_name?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.full_name || 'Admin User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || 'admin@example.com'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate('/profile')}>
              <PersonIcon sx={{ mr: 1.5, fontSize: 20 }} />
              {t('header.profile')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
              {t('header.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;